import React from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import Lottie from "lottie-react";
import { motion } from "framer-motion";

// 🔹 Import Lottie JSON animations
import missionGif from "../../assets/about/mission.json";
import visionGif from "../../assets/about/vision.json";
import heroAnim from "../../assets/about/hero.json";

const coreValues = [
  { title: "Innovation", color: "linear-gradient(to right, #272626e9, #7e7b7be9, #f7f4f4e9, #ffffff)" },
  { title: "Transparency", color: "linear-gradient(to right, #272626e9, #7e7b7be9, #f7f4f4e9, #ffffff)" },
  { title: "Collaboration", color: "linear-gradient(to right, #272626e9, #7e7b7be9, #f7f4f4e9, #ffffff)" },
  { title: "Growth", color: "linear-gradient(to right, #272626e9, #7e7b7be9, #f7f4f4e9, #ffffff)" },
];

const About = () => {
  // detect screen between 900px and 1380px
  const between900and1380 = useMediaQuery("(min-width:900px) and (max-width:1380px)");

  return (
    <Box sx={{ bgcolor: "black", color: "white", overflow: "hidden" }}>
      {/* Desktop View (>=900px) */}
      <Box
        sx={{
          display: { xs: "none", md: "block" },
          maxWidth: "1440px",
          mx: "auto",
          px: 2,
          py: 6,
        }}
      >
        {/* Hero Section */}
        <Grid container>
          <Grid item xs={12} md={6}>
            <Box sx={{ pl: "70px", pt: "141px" }}>
              <Stack spacing={2}>
                <Typography
                  variant="h4"
                  sx={{
                    fontFamily: "Roboto-Bold, Helvetica",
                    fontWeight: "bold",
                    color: "#ffffffa1",
                    fontSize: "36px",
                    lineHeight: "38px",
                  }}
                >
                  About us
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontFamily: "Roboto-Bold, Helvetica",
                    fontWeight: "bold",
                    color: "white",
                    fontSize: "53.8px",
                    lineHeight: "63.4px",
                    maxWidth: "524px",
                    mt: "10px",
                  }}
                >
                  Empowering Every Payment. Every Day.
                </Typography>
              </Stack>

              <Typography
                sx={{
                  fontFamily: "Roboto-Regular, Helvetica",
                  color: "white",
                  fontSize: "24px",
                  lineHeight: "26px",
                  textAlign: "justify",
                  maxWidth: "670px",
                  mt: "50px",
                }}
              >
                At Rubaai, we're building the future of digital payments in Sri
                Lanka. From quick bill payments to secure money transfers and QR
                code transactions we're here to make your financial life easier,
                safer, and smarter.
                <br />
                We believe that everyone should have access to simple, cashless
                solutions - whether you're shopping online, paying your utility
                bills, or supporting your local business.
              </Typography>
            </Box>
          </Grid>

          {/* Hero Illustration */}
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ x: 100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 1 }}
            >
              <Box
                sx={{ width: "100%", overflow: "hidden", textAlign: "center" }}
              >
                <Lottie
                  animationData={heroAnim}
                  loop
                  style={{
                    width: "90%",
                    height: "auto",
                    display: "inline-block",
                    filter: "drop-shadow(0px 0px 20px #ffffffff)",
                  }}
                />
              </Box>
            </motion.div>
          </Grid>
        </Grid>

        {/* Mission & Vision Section */}
        <Box sx={{ mt: "50px", textAlign: "center" }}>
          <Typography
            sx={{
              fontFamily: "Roboto-Bold, Helvetica",
              fontWeight: "bold",
              color: "#ffffff9e",
              fontSize: "36px",
              mb: "13px",
            }}
          >
            Mission & Vision
          </Typography>

          <Typography
            sx={{
              fontFamily: "Roboto-Light, Helvetica",
              fontWeight: 300,
              color: "white",
              fontSize: "36px",
              WebkitTextStroke: "1px #ffffff",
              maxWidth: "1152px",
              mx: "auto",
            }}
          >
            Our purpose and direction driving innovation, trust, and financial
            empowerment.
          </Typography>
        </Box>

        {/* Mission & Vision Cards */}
        <Box sx={{ position: "relative", mt: "30px", height: "596px" }}>
          {/* Vertical Line */}
          <Box
            sx={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              width: "24px",
              height: "596px",
              bgcolor: "#ccc",
            }}
          />

          {/* Mission Card */}
          <Paper
            sx={{
              position: "absolute",
              top: 0,
              right: "57px",
              width: "604px",
              height: "282px",
              bgcolor: "#d9d9d9",
              borderRadius: "0 100px 100px 0",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Roboto-Light, Helvetica",
                fontWeight: 300,
                fontSize: "36px",
                WebkitTextStroke: "1px #000",
                mb: 2,
              }}
            >
              OUR MISSION
            </Typography>
            <Typography
              sx={{
                fontFamily: "Roboto-Medium, Helvetica",
                fontSize: "24px",
                textAlign: "center",
                maxWidth: "516px",
              }}
            >
              To make digital payments simple, trusted, and accessible for every Sri Lankan  so that managing money feels effortless, regardless of who you are or where you are.
            </Typography>
          </Paper>

          {/* Vision Card */}
          <Paper
            sx={{
              position: "absolute",
              bottom: 0,
              left: "57px",
              width: "604px",
              height: "282px",
              bgcolor: "#d9d9d9",
              borderRadius: "100px 0 0 100px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              p: 2,
            }}
          >
            <Typography
              sx={{
                fontFamily: "Roboto-Light, Helvetica",
                fontWeight: 300,
                fontSize: "36px",
                WebkitTextStroke: "1px #000",
                mb: 2,
              }}
            >
              OUR VISION
            </Typography>
            <Typography
              sx={{
                fontFamily: "Roboto-Medium, Helvetica",
                fontSize: "24px",
                textAlign: "center",
                maxWidth: "516px",
              }}
            >
              To be Sri Lanka's most trusted digital wallet , the financial layer that powers everyday life for millions of people.
            </Typography>
          </Paper>

          {/* Mission Animation */}
          {!between900and1380 && (
            <Box
              sx={{
                position: "absolute",
                top: "-10px",
                left: "130px",
                width: "550px",
                height: "313px",
                borderRadius: "20px",
                overflow: "hidden",
              }}
            >
              <Lottie
                animationData={missionGif}
                loop
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </Box>
          )}

          {/* Vision Animation */}
          {!between900and1380 && (
            <Box
              sx={{
                position: "absolute",
                bottom: "2px",
                right: "70px",
                width: "600px",
                height: "313px",
                borderRadius: "20px",
                overflow: "hidden",
              }}
            >
              <Lottie
                animationData={visionGif}
                loop
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </Box>
          )}
        </Box>

        {/* Core Values Section */}
        <Box sx={{ mt: "120px", textAlign: "center" }}>
          <Typography
            sx={{
              fontFamily: "Roboto-Bold, Helvetica",
              fontWeight: "bold",
              color: "#ffffff9e",
              fontSize: "36px",
              mb: "13px",
            }}
          >
            Our Core Values
          </Typography>

          <Typography
            sx={{
              fontFamily: "Roboto-Light, Helvetica",
              fontWeight: 300,
              fontSize: "36px",
              WebkitTextStroke: "1px #fff",
              maxWidth: "1152px",
              mx: "auto",
              mb: "80px",
            }}
          >
            These values guide our approach to creating innovative FinTech
            solutions.
          </Typography>
        </Box>

        {/* Core Values Cards */}
        <Stack
          direction="row"
          spacing={2}
          sx={{
            gap: "20px",
            px: "57px",
            mt: "80px",
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          {coreValues.map((value, i) => (
            <Card
              key={i}
              sx={{
                width: "292px",
                height: "181px",
                background: value.color,
                borderRadius: "30px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                m: 1,
              }}
            >
              <CardContent sx={{ textAlign: "center", p: 0 }}>
                <Typography
                  sx={{
                    fontFamily: "Roboto-Light, Helvetica",
                    fontWeight: 300,
                    color: "black",
                    fontSize: "32px",
                    WebkitTextStroke: "1px #000",
                  }}
                >
                  {value.title}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Stack>
      </Box>

      {/* Mobile View (<900px) */}
      <Box
        sx={{
          display: { xs: "block", md: "none" },
          px: 2,
          py: 6,
          maxWidth: "100%",
          textAlign: "center",
        }}
      >
        {/* Hero Section */}
        <Typography
          sx={{ mt: 6, fontSize: "18px", fontWeight: 700, color: "#9e9e9e" }}
        >
          About us
        </Typography>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "24px",
            my: 2,
          }}
        >
          Empowering Every Payment. Every Day.
        </Typography>

        <Lottie
          animationData={heroAnim}
          loop
          style={{
            width: "60%",
            height: "auto",
            display: "inline-block",
            filter: "drop-shadow(0px 0px 20px #ffffffff)",
          }}
        />

        <Typography
          sx={{
            fontSize: "14px",
            mt: 1,
            textAlign: "justify",
          }}
        >
          At Rubaai, we're building the future of digital payments in Sri Lanka.
          From quick bill payments to secure money transfers and QR code
          transactions we're here to make your financial life easier, safer, and
          smarter.
          <br />
          We believe that everyone should have access to simple, cashless
          solutions — whether you're shopping online, paying your utility bills,
          or supporting your local business.
        </Typography>

        {/* Mission & Vision Section */}
        <Typography
          sx={{ mt: 6, fontSize: "18px", fontWeight: 700, color: "#9e9e9e" }}
        >
          Mission & Vision
        </Typography>
        <Typography
          sx={{
            fontSize: "20px",
            fontWeight: 500,
          }}
        >
          Our purpose and direction driving innovation, trust, and financial
          empowerment.
        </Typography>

        {/* Mission Card */}
        <Card sx={{ my: 3, bgcolor: "#d9d9d9" }}>
          <CardContent>
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: "24px",
                mb: 2,
              }}
            >
              Our Mission
            </Typography>
            <Typography sx={{ fontSize: "14px" }}>
              To be a trailblazer in digital financial innovation by delivering
              smart, secure, and scalable technology solutions that transform
              the way individuals and businesses manage financial transactions.
            </Typography>
          </CardContent>
        </Card>

        {/* Vision Card */}
        <Card sx={{ my: 3, bgcolor: "#d9d9d9" }}>
          <CardContent>
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: "24px",
                mb: 2,
              }}
            >
              Our Vision
            </Typography>
            <Typography sx={{ fontSize: "14px" }}>
              To deliver innovative, reliable, and scalable IT solutions that
              simplify complex challenges, enhance digital experiences, and
              drive growth for individuals, businesses, and communities across
              Sri Lanka.
            </Typography>
          </CardContent>
        </Card>

        {/* Core Values */}
        <Typography
          sx={{ mt: 6, fontSize: "18px", fontWeight: 700, color: "#9e9e9e" }}
        >
          Our Core Values
        </Typography>
        <Typography
          sx={{
            fontSize: "20px",
            fontWeight: 500,
          }}
        >
          These values guide our approach to creating innovative FinTech
          solutions.
        </Typography>
        <Stack spacing={2} sx={{ mt: 3, alignItems: "center" }}>
          {coreValues.map((value, i) => (
            <Card
              key={i}
              sx={{
                width: "282px",
                height: "144px",
                background: value.color,
                borderRadius: "30px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: "24px",
                  WebkitTextStroke: "1px #000",
                }}
              >
                {value.title}
              </Typography>
            </Card>
          ))}
        </Stack>
      </Box>
    </Box>
  );
};

export default About;
